/**
 * In-browser ring buffer of feed-layer snapshots for the timeline scrubber.
 * Stores the actual Track arrays (by reference) sampled over time, so historical
 * playback is instant and full-fidelity over the observed session. Deep/long-term
 * history is persisted server-side (track_history) and served via /api/history.
 */

import type { Track } from '@vtak/shared';

/** A timestamped snapshot of one layer. */
interface Sample {
  t: number;
  tracks: Track[];
}

/** How far back the buffer retains samples. */
const RETENTION_MS = 1_800_000;

/** Hard memory bounds: layers tracked and samples kept per layer. */
const MAX_LAYERS = 16;
const MAX_SAMPLES_PER_LAYER = 720;

/** Per-layer sample history (Map iteration order doubles as insertion LRU). */
const buffers = new Map<string, Sample[]>();

/** Evict the oldest-inserted layer when the layer cap is exceeded. */
function evictOldestLayer(): void {
  const oldest = buffers.keys().next();
  if (!oldest.done) buffers.delete(oldest.value);
}

/** Append a snapshot for a layer and drop samples beyond retention/size bounds. */
export function recordSample(layer: string, tracks: Track[], t: number): void {
  let samples = buffers.get(layer);
  if (!samples) {
    if (buffers.size >= MAX_LAYERS) evictOldestLayer();
    samples = [];
    buffers.set(layer, samples);
  }
  samples.push({ t, tracks });
  const cutoff = t - RETENTION_MS;
  while (samples.length > 0 && (samples[0]?.t ?? Infinity) < cutoff) samples.shift();
  while (samples.length > MAX_SAMPLES_PER_LAYER) samples.shift();
}

/** The latest snapshot for a layer at or before time `t` (empty if none). */
export function snapshotAt(layer: string, t: number): Track[] {
  const samples = buffers.get(layer);
  if (!samples || samples.length === 0) return [];
  let result: Track[] = [];
  for (const sample of samples) {
    if (sample.t > t) break;
    result = sample.tracks;
  }
  return result;
}

/** Earliest sample time across all layers (now if empty). */
export function earliestSample(now: number): number {
  let earliest = now;
  for (const samples of buffers.values()) {
    const first = samples[0];
    if (first && first.t < earliest) earliest = first.t;
  }
  return earliest;
}

/** Clear the buffer (on teardown). */
export function clearBuffer(): void {
  buffers.clear();
}
